import React from 'react';
import { useSimulatorStore } from '../../store/simulatorStore';
import { AgeMode } from '../../types';
import { PRESET_EXPERIMENTS } from '../../experiments/presets';
import { AppMode } from '../../App';

const AGE_MODES: { value: AgeMode; label: string; desc: string; color: string }[] = [
  { value: 'beginner', label: '🌱 Beginner', desc: 'Ages 8–10', color: '#10b981' },
  { value: 'intermediate', label: '🔬 Intermediate', desc: 'Ages 11–13', color: '#6366f1' },
  { value: 'advanced', label: '⚛️ Advanced', desc: 'Ages 14–18', color: '#f59e0b' },
];

interface Props {
  mode: AppMode;
  onModeChange: (m: AppMode) => void;
}

export default function TopBar({ mode, onModeChange }: Props) {
  const {
    ageMode,
    setAgeMode,
    status,
    numParticles,
    setNumParticles,
    runSimulation,
    resetSimulation,
    stepSimulation,
    loadPreset,
    activePreset,
    nodes,
    toggleHint,
    showHint,
  } = useSimulatorStore();

  const filteredPresets = PRESET_EXPERIMENTS.filter((p) =>
    p.ageMode.includes(ageMode)
  );

  const isRunning = (status as string) === 'running';
  const canRun = nodes.length > 0 && !isRunning;

  return (
    <div
      style={{
        height: 56,
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 16px',
        fontFamily: 'system-ui, sans-serif',
        flexShrink: 0,
        zIndex: 10,
      }}
    >
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 8 }}>
        <span style={{ fontSize: 22 }}>⚛️</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
            QuantumLab
          </div>
          <div style={{ fontSize: 9, color: '#94a3b8', lineHeight: 1 }}>
            Quantum Simulator by Macromia.ai
          </div>
        </div>
      </div>

      <Separator />

      {/* Mode toggle */}
      <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 2, gap: 2 }}>
        {([
          { id: 'lab', label: '🔬 Experiment Lab' },
          { id: 'circuit', label: '⚡ Circuit Builder' },
        ] as const).map(m => (
          <button
            key={m.id}
            onClick={() => onModeChange(m.id)}
            style={{
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: mode === m.id ? 700 : 400,
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              background: mode === m.id ? 'white' : 'transparent',
              color: mode === m.id ? '#1e293b' : '#64748b',
              boxShadow: mode === m.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      <Separator />

      {/* ── Lab-only controls ── */}
      {mode === 'lab' && (
        <>
          {/* Age mode selector */}
          <div style={{ display: 'flex', gap: 2 }}>
            {AGE_MODES.map((m) => (
              <button
                key={m.value}
                onClick={() => setAgeMode(m.value)}
                title={m.desc}
                style={{
                  padding: '4px 10px',
                  fontSize: 11,
                  border: `1.5px solid ${ageMode === m.value ? m.color : '#e2e8f0'}`,
                  borderRadius: 20,
                  cursor: 'pointer',
                  background: ageMode === m.value ? m.color + '22' : 'white',
                  color: ageMode === m.value ? m.color : '#64748b',
                  fontWeight: ageMode === m.value ? 700 : 400,
                  transition: 'all 0.12s',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          <Separator />

          {/* Preset selector */}
          <select
            value={activePreset?.id ?? ''}
            onChange={(e) => e.target.value && loadPreset(e.target.value)}
            style={{
              padding: '5px 10px',
              fontSize: 11,
              border: '1.5px solid #e2e8f0',
              borderRadius: 8,
              cursor: 'pointer',
              background: 'white',
              color: '#374151',
              maxWidth: 200,
            }}
          >
            <option value="">📋 Choose Experiment…</option>
            {filteredPresets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <Separator />

          {/* Particle count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
              Particles:
            </span>
            <select
              value={numParticles}
              onChange={(e) => setNumParticles(Number(e.target.value))}
              style={{
                padding: '4px 8px',
                fontSize: 11,
                border: '1.5px solid #e2e8f0',
                borderRadius: 6,
                background: 'white',
                color: '#374151',
              }}
            >
              {[50, 100, 200, 500, 1000].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <Separator />

          {/* Run / Step / Reset */}
          <div style={{ display: 'flex', gap: 6 }}>
            <ControlButton onClick={stepSimulation} disabled={!canRun} title="Step" color="#6366f1" outline>
              ⏭ Step
            </ControlButton>
            <ControlButton onClick={runSimulation} disabled={!canRun} title="Run" color="#6366f1">
              {isRunning ? '⏳ Running…' : '▶ Run'}
            </ControlButton>
            <ControlButton onClick={resetSimulation} disabled={isRunning} title="Reset" color="#64748b" outline>
              ↺ Reset
            </ControlButton>
          </div>

          {/* Hint toggle */}
          <div style={{ marginLeft: 'auto' }}>
            <button
              onClick={toggleHint}
              style={{
                padding: '5px 12px',
                fontSize: 11,
                border: `1.5px solid ${showHint ? '#f59e0b' : '#e2e8f0'}`,
                borderRadius: 20,
                cursor: 'pointer',
                background: showHint ? '#fef3c7' : 'white',
                color: showHint ? '#b45309' : '#64748b',
                fontWeight: 600,
              }}
            >
              💡 Hints
            </button>
          </div>
        </>
      )}

      {/* ── Circuit-mode badge ── */}
      {mode === 'circuit' && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
            background: '#ede9fe', color: '#6366f1', border: '1px solid #c4b5fd',
          }}>
            ⚡ State Vector Simulation · up to 3 qubits · exact math
          </div>
        </div>
      )}
    </div>
  );
}

function Separator() {
  return (
    <div style={{ width: 1, height: 28, background: '#e2e8f0', flexShrink: 0, margin: '0 4px' }} />
  );
}

function ControlButton({
  children, onClick, disabled, title, color, outline,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  color: string;
  outline?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '6px 14px',
        fontSize: 12,
        fontWeight: 600,
        border: `1.5px solid ${disabled ? '#e2e8f0' : color}`,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: outline ? 'white' : disabled ? '#f1f5f9' : color,
        color: outline ? (disabled ? '#94a3b8' : color) : disabled ? '#94a3b8' : 'white',
        transition: 'all 0.12s',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
}
